import { Formik, Field, Form, ErrorMessage } from "formik";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { LOGIN_VALIDATION } from "./Validations";
import { loginUser } from "../../api/loginAndReg";
import { useNotifications } from "../../api/useNotifications";
import { useAuthContext } from "../../contexts/authContext";

interface LoginValues {
  email: string;
  password: string;
}

interface LoginFormProps {
  disabled?: boolean;
  email?: string | null;
}

const LoginForm = ({ disabled, email }: LoginFormProps) => {
  const initialValues: LoginValues = {
    email: email || "",
    password: "",
  };

  const { setUser } = useAuthContext();

  const location = useLocation();
  const isLogin = location.pathname === "/login";
  const isHome = location.pathname === "/";

  const navigate = useNavigate();

  const { subscribeToNotifications } = useNotifications();

  const onSubmit = async (
    values: LoginValues,
    {
      setSubmitting,
      setFieldError,
    }: {
      setSubmitting: (isSubmitting: boolean) => void;
      setFieldError: (field: string, message: string) => void;
    }
  ) => {
    try {
      const { token, user } = await loginUser(values);
      localStorage.setItem("token", token);
      localStorage.setItem("userType", user.type.toUpperCase());

      setUser(user);

      navigate("/", { replace: true });

      const permission = await Notification.requestPermission();

      if (permission === "granted") {
        await subscribeToNotifications();
      } else {
      }
    } catch (error: any) {
      const message = error?.response?.data?.error;
      if (message === "User not found") {
        setFieldError(
          "email",
          "There’s no account associated with this email."
        );
      } else if (message === "Incorrect password") {
        setFieldError(
          "password",
          "Incorrect password. Please check and re-enter."
        );
      } else {
        setFieldError("password", message || "Login failed.");
      }

      setUser(null);
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={LOGIN_VALIDATION}
      onSubmit={onSubmit}
    >
      {({ isValid, touched, values, handleChange, isSubmitting }) => (
        <Form>
          <div className={`relative mb-${isLogin ? "4" : "8"}`}>
            <label
              htmlFor="email"
              className="text-grey-700 block text-sm font-semibold mb-2"
            >
              Email:
            </label>
            <Field
              type="email"
              id="email"
              name="email"
              placeholder="Enter your email"
              className="w-full p-3 border-2 rounded-lg"
              disabled={disabled}
              onChange={handleChange}
            />
            {touched.email && !isValid && (
              <div className="text-red-500">
                <ErrorMessage name="email" />
              </div>
            )}
          </div>

          <div className={`relative mb-${isLogin ? "2" : "4"}`}>
            <label
              htmlFor="password"
              className="block text-sm text-grey-700 font-semibold mb-2"
            >
              Password:
            </label>
            <Field
              type="password"
              id="password"
              name="password"
              placeholder="Enter your password"
              className="w-full p-3 border-2 rounded-lg"
              disabled={disabled}
              onChange={handleChange}
            />
            {touched.password && !isValid && (
              <div className="text-red-500">
                <ErrorMessage name="password" />
              </div>
            )}
          </div>

          <div
            className={`relative mb-6 flex justify-${
              isHome ? "between" : "end"
            }`}
          >
            {isHome && (
              <div className="flex">
                <small className="mr-2 block text-sm text-gray-500 mt-1">
                  Don’t have an account?
                </small>
                <Link className="text-blue-600 nav-link" to={"/register"}>
                  Register
                </Link>
              </div>
            )}

            <div>
              <Link
                className="text-blue-600 nav-link"
                to={"/login/forgot-password"}
              >
                Forgot password?
              </Link>
            </div>
          </div>

          <button
            type="submit"
            disabled={
              !isValid ||
              (values.email === initialValues.email &&
                values.password === initialValues.password)
            }
            className={`w-full py-3 bg-blue-500 text-white font-semibold rounded-lg ${
              !isValid ||
              (values.email === initialValues.email &&
                values.password === initialValues.password)
                ? "bg-[#1672ec] bg-opacity-50"
                : "hover:bg-blue-600"
            }`}
          >
            {isSubmitting ? "Logging in..." : "Login"}
          </button>

          {isLogin && (
            <div className="flex justify-center mt-4">
              <small className="mr-2 block text-sm text-gray-500 mt-1">
                New to BuildConnect?
              </small>
              <Link className="text-blue-600 nav-link" to={"/register"}>
                Register
              </Link>
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default LoginForm;
